/* eslint-disable import/no-unused-modules */
import {
  Button,
  ButtonGroup,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Toggle,
} from '@nike/eds';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import * as R from 'ramda';
import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { MultiValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDebouncedCallback } from 'use-debounce';
import {
  rewriteCreateModalAtom,
  rewriteUpdateHasErrorAtom,
  rewriteUpdateMessageAtom,
} from '../../atoms/rewrite';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import RewriteClient from '../../clientAPI/rewrite';
import TagsInput from '../../components/RewriteTypoShared/TagsInput';
import {
  QUERY_KEYS,
  RELATION_TYPE,
  REWRITE_T2C_TARGET_SOURCE,
  REWRITE_TYPE,
} from '../../constants';
import RewriteTermInput from './RewriteTermInput';

export default function TermItemCreate({ type }: { type: 'rewrite' }) {
  const queryClient = useQueryClient();
  const [visible, setVisible] = useAtom(rewriteCreateModalAtom);
  const [updateMsg, setUpdateMsg] = useAtom(rewriteUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    rewriteUpdateHasErrorAtom,
  );
  const [termErrorMessages, setTermErrorMessages] = useState<string[]>(['']);
  const [targetErrorMessage, setTargetErrorMessage] = useState('');
  const [rewriteTagsErrorMessage, setRewriteTagsErrorMessage] = useState('');
  const [termValues, setTermValues] = useState<string[]>(['']);
  const [textValue, setTextValue] = useState('');
  const [rewriteType, setRewriteType] = useState(REWRITE_TYPE.T2T);
  const [relationType, setRelationType] = useState(RELATION_TYPE.AND);
  const [includeOriginal, setIncludeOriginal] = useState(true);
  const [selectedConcepts, setSelectedConcepts] = useState<RewriteTarget[]>([]);

  // const [resetKey, setResetKey] = useState(0);
  const [resetKey, setResetKey] = useState(Date.now());

  const revalidator = useRevalidator();
  const queryKey = QUERY_KEYS.REWRITE_LIST;
  const source = REWRITE_T2C_TARGET_SOURCE;
  const apiClient = RewriteClient;

  const [rewriteTags, setRewriteTags] = useState<RewriteTag[]>([]);
  const [termValueValidations, setTermValueValidations] = useState<{
    [key: string]: boolean;
  }>({});

  const handleRewriteTagsChange = (newTags: RewriteTag[]) => {
    setRewriteTags(newTags);
  };

  const mutation = useMutation({
    mutationFn: (data: RewriteCreatePayload): Promise<RewriteItem> =>
      apiClient.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
      setUpdateHasError(false);
      setUpdateMsg('Created!');
    },
    onError: (err: keyable) => {
      setUpdateHasError(true);
      if (err.status === 409) {
        setUpdateMsg(
          'This rewrite already exists. Please check the existing entries.',
        );
      } else {
        setUpdateMsg(err.message || JSON.stringify(err));
      }
      err.status === 401 && revalidator.revalidate();
    },
  });

  const resetModal = () => {
    setRewriteType(REWRITE_TYPE.T2T);
    setTermValues(['']);
    setTermValueValidations({});
    setTextValue('');
    setTermErrorMessages(['']);
    setTargetErrorMessage('');
    setRewriteTagsErrorMessage('');
    setResetKey(Date.now());
    setRelationType(RELATION_TYPE.AND);
  };
  const onSubmitBtnClick = () => {
    const validTerms = termValues.filter((t) => t && termValueValidations[t]);
    if (validTerms.length === 0) {
      return setTermErrorMessages(['Required!']);
    }
    if (rewriteType === REWRITE_TYPE.T2T && !textValue) {
      return setTargetErrorMessage('Required!');
    }
    if (rewriteType === REWRITE_TYPE.T2C && selectedConcepts.length === 0) {
      return setTargetErrorMessage('Required!');
    }
    const hasValidRewriteTag = rewriteTags.some((rt) =>
      ['normal', 'typo'].includes(rt.tagName.toLowerCase()),
    );

    const hasNormalTag = rewriteTags.some(
      (rt) => rt.tagName.toLowerCase() === 'normal',
    );
    const hasTypoTag = rewriteTags.some(
      (rt) => rt.tagName.toLowerCase() === 'typo',
    );

    const hasConflictingTags = hasNormalTag && hasTypoTag;

    if (rewriteTags.length === 0 || !hasValidRewriteTag) {
      return setRewriteTagsErrorMessage(
        'Required, At least set rewriteTag to "normal" or "typo"!',
      );
    } else if (hasConflictingTags) {
      return setRewriteTagsErrorMessage(
        'Invalid configuration: A rewrite cannot be both "normal" and "typo"',
      );
    }

    for (const term of validTerms) {
      const payload =
        rewriteType === REWRITE_TYPE.T2T
          ? {
              term,
              type: REWRITE_TYPE.T2T,
              target: textValue,
              rewriteTags: rewriteTags,
            }
          : {
              term,
              type: REWRITE_TYPE.T2C,
              target: selectedConcepts,
              relationType: relationType,
              rewriteTags: rewriteTags,
            };
      if (type === 'rewrite') {
        (payload as RewriteCreatePayload).includeOriginal = includeOriginal;
      }
      mutation.mutate(payload as RewriteCreatePayload);
    }
    onDismissModal();
  };
  const onDismissModal = () => {
    resetModal();
    setVisible(false);
  };

  const asyncOptions = useDebouncedCallback((inputValue, callback) => {
    RecommendConceptsClient.searchConcepts(inputValue)
      .then((res) => {
        const opts = res.objects.map((c) => {
          const label =
            c.name === c.alternateName
              ? `${c.name} ${c.source.system}`
              : `${c.name}(${c.alternateName}) ${c.source.system}`;
          return {
            value: {
              id: c.id,
              parentId: c.parentId,
              name: c.name,
              source,
            },
            label,
          };
        });
        callback(opts);
      })
      .catch((err: { status: number }) => {
        console.log(err);
        err.status === 401 && revalidator.revalidate();
        callback([]);
      });
  }, 1000);

  const handleSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConcepts = R.uniqBy(
      R.prop('id'),
      newSelectOpts.map((opt) => opt.value),
    );
    console.log(selectedConcepts);
    setSelectedConcepts(selectedConcepts as RewriteTarget[]);
  };
  const shouldDisableSubmit = termValues.some(
    (term) => term !== '' && !termValueValidations[term],
  );
  return (
    <Modal
      onDismiss={onDismissModal}
      isOpen={visible}
      headerSlot={'New Term(s) Rewrite'}
    >
      <div
        className='tw-flex tw-flex-col tw-justify-center'
        style={{ width: '40rem' }}
      >
        <div className='tw-mb-10 tw-pb-20'>
          <div className='tw-mb-2'>
            {termValues.map((term, index) => (
              <div
                key={`${index}_${term}`}
                className='tw-flex tw-items-start tw-gap-2 tw-mb-2'
              >
                <div className='tw-flex-1'>
                  <RewriteTermInput
                    termValue={term}
                    termErrorMessage={termErrorMessages[index] || ''}
                    handleTermValueChange={(val) => {
                      setTermValues((prev) =>
                        prev.map((t, i) => (i === index ? val : t)),
                      );
                      setTermErrorMessages((prev) =>
                        prev.map((e, i) => (i === index ? '' : e)),
                      );
                    }}
                    hanldeTermValueValidation={(val, validated) => {
                      setTermValueValidations((prev) => ({
                        ...prev,
                        [val]: validated,
                      }));
                    }}
                  />
                </div>
                {termValues.length > 1 && (
                  <div className='tw-px-2 tw-pt-4'>
                    <IconButton
                      icon='Minus'
                      label='Remove term'
                      title='Remove term'
                      variant='ghost'
                      size='small'
                      onClick={() => {
                        setTermValues((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                        setTermErrorMessages((prev) =>
                          prev.filter((_, i) => i !== index),
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
            <IconButton
              icon='Plus'
              label='Add term'
              title='Add term'
              variant='secondary'
              size='small'
              onClick={() => {
                setTermValues((prev) => [...prev, '']);
                setTermErrorMessages((prev) => [...prev, '']);
              }}
            />
          </div>
          <RadioGroup
            id='rewrite-type-create'
            label=''
            orientation='horizontal'
            name='radio-button-group-rewrite-create'
            valueSelected={rewriteType}
            onChange={(event) => {
              const { target } = event;
              const val = (target as HTMLInputElement).value;
              setRewriteType(val);
            }}
          >
            <Radio
              label='Text To Text'
              value={REWRITE_TYPE.T2T}
              id='rewrite-type-1-create'
            />
            <Radio
              label='Text To Concepts'
              value={REWRITE_TYPE.T2C}
              id='rewrite-type-2-create'
            />
          </RadioGroup>
          {rewriteType === REWRITE_TYPE.T2T && (
            <TextField
              type='text'
              value={textValue}
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setTextValue(val);
                setTargetErrorMessage('');
              }}
              id='rewrite-text-target-create'
              placeholder='target term'
              label=''
              hasErrors={targetErrorMessage !== ''}
              errorMessage={targetErrorMessage}
            />
          )}
          {rewriteType === REWRITE_TYPE.T2C && (
            <div className='eds-select tw-mt-4'>
              <AsyncSelect
                className='eds-text-field__input eds-text-field--select'
                classNamePrefix='eds-select'
                isMulti
                cacheOptions
                loadOptions={asyncOptions}
                onChange={handleSelectChange}
              />
              <RadioGroup
                id='relation-type-create'
                label=''
                orientation='horizontal'
                name='radio-button-group-relation-type-create'
                valueSelected={relationType}
                onChange={(event) => {
                  const { target } = event;
                  const val = (target as HTMLInputElement).value;
                  setRelationType(val);
                }}
              >
                <Radio
                  label='AND'
                  value={RELATION_TYPE.AND}
                  id='relation-type-1-create'
                />
                <Radio
                  label='OR'
                  value={RELATION_TYPE.OR}
                  id='relation-type-2-create'
                />
              </RadioGroup>
            </div>
          )}
          {type === 'rewrite' && (
            <div className='eds-select tw-mt-4'>
              <Toggle
                label='Include Original Term'
                id='rewrite-include-original-term-create'
                onChange={(event) => {
                  const { target } = event;
                  const val = (target as HTMLInputElement).checked;
                  setIncludeOriginal(val);
                }}
              />
            </div>
          )}
          <TagsInput
            onRewriteTagsChange={handleRewriteTagsChange}
            resetKey={resetKey}
            parentComponentName={'termItemCreate'}
            rewriteTagsErrorMessage={rewriteTagsErrorMessage}
            setRewriteTagsErrorMessage={setRewriteTagsErrorMessage}
          />
        </div>
        <ButtonGroup>
          <Button
            onClick={onSubmitBtnClick}
            disabled={shouldDisableSubmit}
            size='small'
            variant='secondary'
            className={`eds-color--white eds-border--green-60 ${
              shouldDisableSubmit
                ? 'eds-opacity-50'
                : 'eds-background--green-60'
            }`}
          >
            Create
          </Button>
          <Button onClick={onDismissModal} size='small' variant='secondary'>
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
}
